import React from "react";
import * as Popover from "@radix-ui/react-popover";
import * as ScrollArea from "@radix-ui/react-scroll-area";
import { cn } from "src/lib/cn";
import { motion, AnimatePresence } from "framer-motion";

export type Side = "bottom" | "top" | "right" | "left";
export type Align = "start" | "center" | "end";

export interface GenericPopoverProps {
  children: React.ReactNode;
  trigger: React.ReactNode;
  className?: string;
  triggerClassName?: string;
  contentClassName?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  side?: Side;
  align?: Align;
  sideOffset?: number;
  alignOffset?: number;
  showArrow?: boolean;
  arrowClassName?: string;
  maxHeight?: string;
  width?: string;
  closeOnContentClick?: boolean;
  modal?: boolean;
  animated?: boolean;
}

const GenericPopover = React.forwardRef<HTMLButtonElement, GenericPopoverProps>(
  (
    {
      children,
      trigger,
      className,
      triggerClassName,
      contentClassName,
      open,
      onOpenChange,
      side = "bottom",
      align = "center",
      sideOffset = 5,
      alignOffset = 0,
      showArrow = false,
      arrowClassName,
      maxHeight = "400px",
      width,
      closeOnContentClick = false,
      modal = true,
      animated = true,
    },
    ref,
  ) => {
    const [isOpen, setIsOpen] = React.useState(open ?? false);

    React.useEffect(() => {
      if (open !== undefined) {
        setIsOpen(open);
      }
    }, [open]);

    const handleOpenChange = React.useCallback(
      (newOpen: boolean) => {
        if (open === undefined) {
          setIsOpen(newOpen);
        }
        onOpenChange?.(newOpen);
      },
      [open, onOpenChange],
    );

    const handleContentClick = React.useCallback(() => {
      if (closeOnContentClick) {
        handleOpenChange(false);
      }
    }, [closeOnContentClick, handleOpenChange]);

    const contentElement = (
      <div
        className={cn("relative", contentClassName)}
        style={{
          maxHeight: maxHeight || undefined,
          width: width || undefined,
        }}
        onClick={handleContentClick}
      >
        {maxHeight && maxHeight !== "auto" && maxHeight !== "100%" ? (
          <ScrollArea.Root
            className="h-full w-full"
            type="auto"
          >
            <ScrollArea.Viewport className={cn("h-full w-full", maxHeight && maxHeight !== "auto" && maxHeight !== "100%" ? `max-h-[${maxHeight}]` : "max-h-full")}>{children}</ScrollArea.Viewport>
            <ScrollArea.Scrollbar
              className="flex select-none touch-none p-0.5 bg-transparent transition-colors transition-duration-[160ms] ease-out data-[orientation=vertical]:w-2.5 data-[orientation=horizontal]:flex-col data-[orientation=horizontal]:h-2.5"
              orientation="vertical"
            >
              <ScrollArea.Thumb className="flex-1 bg-app-grey-2 rounded-[10px] relative before:content-[''] before:absolute before:top-1/2 before:left-1/2 before:transform before:-translate-x-1/2 before:-translate-y-1/2 before:w-full before:h-full before:min-w-[44px] before:min-h-[44px]" />
            </ScrollArea.Scrollbar>
          </ScrollArea.Root>
        ) : (
          children
        )}
      </div>
    );

    return (
      <Popover.Root
        open={isOpen}
        onOpenChange={handleOpenChange}
        modal={modal}
      >
        <Popover.Trigger
          ref={ref}
          asChild
          className={triggerClassName}
        >
          {trigger}
        </Popover.Trigger>

        <AnimatePresence>
          {isOpen && (
            <>
              {modal && (
                <div
                  className="fixed inset-0 z-[99] pointer-events-auto"
                  onClick={() => handleOpenChange(false)}
                  aria-hidden="true"
                  style={{ backgroundColor: "transparent" }}
                />
              )}
              <Popover.Portal forceMount>
                <Popover.Content
                  side={side}
                  align={align}
                  sideOffset={sideOffset}
                  alignOffset={alignOffset}
                  onInteractOutside={(e) => {
                    if (!modal) {
                      e.preventDefault();
                    }
                  }}
                  className={cn("z-[100] bg-app-menu-color shadow-card", "border border-app-menu-color-hover rounded-xl", "outline-none", className)}
                  asChild
                  forceMount
                >
                  {animated ? (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{
                        duration: 0.15,
                        ease: [0.16, 1, 0.3, 1],
                      }}
                    >
                      {contentElement}
                      {showArrow && <Popover.Arrow className={cn("fill-app-menu-color-hover", arrowClassName)} />}
                    </motion.div>
                  ) : (
                    <div>
                      {contentElement}
                      {showArrow && <Popover.Arrow className={cn("fill-app-menu-color-hover", arrowClassName)} />}
                    </div>
                  )}
                </Popover.Content>
              </Popover.Portal>
            </>
          )}
        </AnimatePresence>
      </Popover.Root>
    );
  },
);

GenericPopover.displayName = "GenericPopover";

export default GenericPopover;
